import { useOutletContext } from 'react-router-dom';

import { ROOT_FOLDER_PATH } from '@/common/constants';
import { ClientMatterWithDetails, DataRoomClientFile, User } from '@/common/types';

export type IOverviewContext = {
  matter: ClientMatterWithDetails;
  user: User;
  isQueryInProgress: boolean;
  setIsQueryInProgress: (val: boolean) => void;
  userMessage: string;
  setUserMessage: (val: string) => void;
  setIsPanelCollapsed: (val: boolean) => void;
  isPanelCollapsed: boolean;
  selectedReferenceFocus: string;
  setSelectedReferenceFocus: (val: string) => void;
  selectedRefHighlight: number[];
  setSelectedRefHighlight: (highlight: number[]) => void;
  toolProgress: (
    startTime: Date | undefined,
    finishTime: Date | undefined,
    currentDate: Date | undefined,
  ) => number;
  getToolProcessing: (toolIdentifier: string) => {
    startTime: Date | undefined;
    finishTime: Date | undefined;
    complete: boolean;
  };
  checkForTool: (toolIdentifier: string) => boolean;
  isSettingsModalOpen: boolean;
  setIsSettingsModalOpen: (val: boolean) => void;
  settingsAnchorTab: string;
  setSettingsAnchorTag: (val: string) => void;
  isUserCurrentEditor: boolean;
};

export type FormatTemplate =
  | 'YYYY.MM.DD'
  | 'YYYY-MM-DD'
  | 'Month DD, YYYY'
  | 'DD Month YYYY'
  | 'MM-DD-YYYY';

type FormatTemplateRecord = Record<FormatTemplate, null>;
const formatTemplateObject: FormatTemplateRecord = {
  'YYYY.MM.DD': null,
  'YYYY-MM-DD': null,
  'Month DD, YYYY': null,
  'DD Month YYYY': null,
  'MM-DD-YYYY': null,
};
export const formatTemplateArray = Object.keys(formatTemplateObject).sort();

export const formatDate = (inputDate: Date | null, formatTemplate: FormatTemplate) => {
  if (inputDate === null) return null;
  const date = new Date(inputDate);

  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const monthName = date.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
  const day = date.getUTCDate().toString().padStart(2, '0');
  switch (formatTemplate) {
    case 'DD Month YYYY':
      return `${day} ${monthName} ${year}`;
    case 'Month DD, YYYY':
      return `${monthName} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'YYYY.MM.DD':
      return `${year}.${month}.${day}`;
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    default:
      return `${year}.${month}.${day}`;
  }
};

export const formatFileDisplay = (
  file: Pick<DataRoomClientFile, 'name' | 'date' | 'company' | 'subject' | 'docTitle'>,
  template: string | null | undefined,
) => {
  if (!template) {
    template = '<<date::YYYY.MM.DD::>> <<company_name>> - <<document_title>> (<<counterparties>>)';
  }
  let renamedFile: string = '';
  const extension = '.' + file.name.split('.').slice(-1)[0];
  const dateTypeMatch = template.match(/<<date::.*::>>/g);
  let formattedDate = '' as string | null;
  let hasDate = false;
  if (dateTypeMatch) {
    const dateTypeFormat = dateTypeMatch[0].replace('<<date::', '').replace('::>>', '');
    formattedDate = formatDate(file.date, dateTypeFormat as FormatTemplate);
    hasDate = formattedDate !== null;
  }
  const hasCompany = file.company !== null;
  const hasSubject =
    file.subject.filter((subject) => !!subject).length > 0 &&
    file.subject.filter((subject) => !!subject).length < 3;
  const subjectJoinedString = file.subject.join(', ');

  renamedFile = template.replace(/<<date::.*::>>/g, hasDate ? formattedDate || '' : '');
  renamedFile = renamedFile.replace('<<company_name>>', hasCompany ? file.company || '' : '');
  renamedFile = renamedFile.replace('<<document_title>>', file.docTitle || '');
  renamedFile = renamedFile.replace('<<counterparties>>', hasSubject ? subjectJoinedString : '');
  // remove paired parens, brackets, and braces
  let output = renamedFile;
  while (output != (output = output.replaceAll(/\(\s*\)|\[\s*\]|\{\s*\}/g, '')));
  renamedFile = output;
  // remove invalid starting and ending characters
  while (
    renamedFile.length > 0 &&
    !/[a-zA-Z0-9)\]}]/g.test(renamedFile.charAt(renamedFile.length - 1))
  ) {
    renamedFile = renamedFile.substring(0, renamedFile.length - 1);
  }
  while (renamedFile.length > 0 && !/[a-zA-Z0-9([{]/g.test(renamedFile.charAt(0))) {
    renamedFile = renamedFile.substring(1, renamedFile.length);
  }

  // remove doubled whitespace
  renamedFile = renamedFile.replaceAll(/\s+/g, ' ');

  // split into segments
  if (file.docTitle) {
    renamedFile = renamedFile.replace(file.docTitle, '<<<' + file.docTitle + '<<<');
  }
  if (hasDate && formattedDate) {
    renamedFile = renamedFile.replace(formattedDate, '<<<' + formattedDate + '<<<');
  }
  if (hasCompany && file.company) {
    renamedFile = renamedFile.replace(file.company, '<<<' + file.company + '<<<');
  }
  if (hasSubject && file.subject) {
    renamedFile = renamedFile.replace(subjectJoinedString, '<<<' + subjectJoinedString + '<<<');
  }
  const segments = renamedFile.split('<<<');
  const revisedSegments = segments.map((seg) => {
    let newSeg = seg;
    // consider segments without any alphanumeric characters
    if (newSeg.split('').every((char) => !/[a-zA-Z0-9]/g.test(char))) {
      if (newSeg.length > 0) {
        let startsWithParen = false;
        let endsWithParen = false;
        let startsWithSpace = false;
        let endsWithSpace = false;
        let connector = '';
        if (newSeg.charAt(0) === ')') {
          startsWithParen = true;
          newSeg = newSeg.slice(1);
        }
        if (newSeg.length > 0 && newSeg.charAt(newSeg.length - 1) === '(') {
          endsWithParen = true;
        }
        if (newSeg.length > 0 && newSeg.charAt(0) === ' ') {
          startsWithSpace = true;
          newSeg = newSeg.trimStart();
        } else {
          if (newSeg.length === 0 || newSeg.charAt(0) === '(') {
            connector = '';
          } else {
            connector = newSeg.charAt(0);
          }
        }
        if (newSeg.length > 0 && newSeg.charAt(0) !== '(') {
          connector = newSeg.charAt(0);
        }
        if (newSeg.length > 1 && newSeg.charAt(1) === ' ') {
          endsWithSpace = true;
        }
        if (connector != ' ') {
          newSeg =
            (startsWithParen ? ')' : '') +
            (startsWithSpace ? ' ' : '') +
            connector +
            (endsWithSpace ? ' ' : '') +
            (endsWithParen ? '(' : '');
        }
      }
    }
    return newSeg;
  });
  renamedFile = revisedSegments.join('');

  // remove invalid starting and ending characters again
  while (
    renamedFile.length > 0 &&
    !/[a-zA-Z0-9)\]}]/g.test(renamedFile.charAt(renamedFile.length - 1))
  ) {
    renamedFile = renamedFile.substring(0, renamedFile.length - 1);
  }
  while (renamedFile.length > 0 && !/[a-zA-Z0-9({[]/g.test(renamedFile.charAt(0))) {
    renamedFile = renamedFile.substring(1, renamedFile.length);
  }

  // this should never happen but will prevent empty filenames
  if (renamedFile.length === 0) {
    renamedFile = 'Unknown';
  }

  renamedFile = renamedFile + (extension && extension.charAt(0) === '.' ? `${extension}` : '');
  return renamedFile;
};

export const checkFileNameFormat = (
  files: Pick<DataRoomClientFile, 'name' | 'displayName' | 'originalPathname'>[],
) => {
  const allOriginal = files.every((file) => file.originalPathname.includes(file.displayName));
  const allMarveri = files.every((file) => !file.originalPathname.includes(file.displayName));

  if (allOriginal) return 'ORIGINAL';
  else if (allMarveri) return 'MARVERI';
  else return 'MIXED';
};

export type DropdownItems = {
  icon?: string;
  title: string;
  onClick: () => void;
};

export type SortToken = string | number;

export const tokenizeForNaturalSort = (input: string): SortToken[] => {
  const tokens: SortToken[] = [];
  const re = /([A-Za-z]+|\d+|[^A-Za-z\d\s]+)/g; // words, numbers, and punctuation
  const parts = input.normalize('NFKD').match(re); // normalize accents
  if (!parts) return [];

  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      tokens.push(Number(part));
    } else {
      tokens.push(part.toLowerCase());
    }
  }
  return tokens;
};

export const compareNaturally = (
  a: string,
  b: string,
  fallback?: (a: string, b: string) => number,
): number => {
  const aTokens = tokenizeForNaturalSort(a);
  const bTokens = tokenizeForNaturalSort(b);
  const maxLength = Math.max(aTokens.length, bTokens.length);

  for (let i = 0; i < maxLength; i++) {
    const aTok = aTokens[i];
    const bTok = bTokens[i];

    if (aTok === undefined && bTok !== undefined) return -1; // a is shorter
    if (bTok === undefined && aTok !== undefined) return 1; // b is shorter

    if (typeof aTok === 'number' && typeof bTok === 'number') {
      if (aTok !== bTok) return aTok - bTok;
    } else if (typeof aTok === 'string' && typeof bTok === 'string') {
      const cmp = aTok.localeCompare(bTok, undefined, { sensitivity: 'base', numeric: true });
      if (cmp !== 0) return cmp;
    } else {
      // text < number
      return typeof aTok === 'string' ? -1 : 1;
    }
  }

  return fallback ? fallback(a, b) : 0;
};

export const compareCardinalPaths = (a: string, b: string): number => {
  const extractName = (path: string) => path.split('/').pop() || path;

  const nameA = extractName(a).trim();
  const nameB = extractName(b).trim();

  const isTrash = (name: string) => name.toLowerCase().includes('~trash');
  const isRoot = (name: string) => name === ROOT_FOLDER_PATH;
  const isFile = (name: string): boolean => /\.[a-z0-9]+$/i.test(name);

  // 0. Root always first
  if (isRoot(nameA) && !isRoot(nameB)) return -1;
  if (!isRoot(nameA) && isRoot(nameB)) return 1;
  if (isRoot(nameA) && isRoot(nameB)) return 0;

  // 1. Trash always last
  const aIsTrash = isTrash(nameA);
  const bIsTrash = isTrash(nameB);
  if (aIsTrash && !bIsTrash) return 1;
  if (!aIsTrash && bIsTrash) return -1;
  if (aIsTrash && bIsTrash) {
    const trashCmp = compareNaturally(nameA, nameB);
    if (trashCmp !== 0) return trashCmp;
  }

  // 2. Files after folders
  const aIsFile = isFile(nameA);
  const bIsFile = isFile(nameB);
  if (aIsFile && !bIsFile) return 1;
  if (!aIsFile && bIsFile) return -1;

  // 3. Natural sort fallback
  const baseA = nameA.replace(/\.[a-z0-9]+$/i, '');
  const baseB = nameB.replace(/\.[a-z0-9]+$/i, '');
  const pathCmp = compareNaturally(baseA, baseB);
  if (pathCmp !== 0) return pathCmp;

  // 4. Fallback to full path (to break ties consistently)
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
};

export function useOverview() {
  return useOutletContext<IOverviewContext>();
}

// TODO: REFACTOR - Replace with Factory Pattern
//
// This manual mapping duplicates logic from server/src/modules/data-room/common/utils/process-dataroom-files.ts
// which is the source of truth for which operations are created for each tool. This creates maintenance
// burden and potential for bugs when adding new tools or changing operation logic.
//
// INTENDED SOLUTION: Extract operation generation logic from process-dataroom-files.ts into a pure
// factory function that:
// 1. Takes tool type + context (file counts, existing data, etc.)
// 2. Returns array of operation types that WOULD be created
// 3. Can be used by both server (for creation) and client (for error detection)
// 4. Keeps server as single source of truth for tool->operation mapping
//
// Example: getOperationsForTool(toolType, { filesWithoutSummaries: 5, hasCustomClauses: true })
//          returns: [{ type: 'COMPUTE_SUMMARY', target: 'file' }, ...]
//
// This ensures client error detection always matches what server actually creates.
//
// Map tool identifiers to relevant operation types
export const getOperationTypesForTool = (toolId: string): string[] => {
  switch (toolId) {
    case 'MISSING_SIGNATURE':
      return ['IDENTIFY_MISSING_SIGNATURES'];
    case 'MISSING_DOCUMENT':
    case 'MISSING_DOCUMENT_DEBUG':
      return ['RUN_COMPUTE_GRAPH_MISSING_REFERENCES', 'RUN_COMPUTE_GRAPH_MISSING_REFERENCES_DEBUG'];
    case 'CONTRACT_CLAUSES':
    case 'CONTRACT_CLAUSES_OLD':
      return ['RUN_COMPUTE_GRAPH_MA_CLAUSES'];
    case 'FINANCE_REVIEW':
      return ['RUN_COMPUTE_GRAPH_VETO_RIGHTS'];
    case 'PERSONNEL_REPORT':
      return ['RUN_COMPUTE_GRAPH_PERSONNEL_REPORT'];
    case 'COMPANY_OVERVIEW':
      return ['RUN_COMPUTE_GRAPH_COMPANY_OVERVIEW'];
    case 'REDLINE':
      return ['EXTRACT_TEXT_AND_SIGNATURE_PAGES', 'COMPUTE_EMBEDDINGS'];
    case 'RENAME':
    case 'ORGANIZE':
      return [
        'EXTRACT_TEXT_AND_SIGNATURE_PAGES',
        'COMPUTE_EMBEDDINGS',
        'COMPUTE_SUMMARY',
        'COMPUTE_TAGS',
        'VALIDATE_TAGS',
      ];
    default:
      return [];
  }
};

// Get errored operations specific to the client matter (not file-level)
export const getErroredOperationsForClientMatter = (
  toolIdentifier: string,
  matter: ClientMatterWithDetails,
) => {
  const relevantOperationTypes = getOperationTypesForTool(toolIdentifier);
  const erroredOperations: Array<{
    id: string;
    operationType: string;
    operationStatus: string;
  }> = [];

  // Check operations on client matter level only
  if (matter?.operation && Array.isArray(matter.operation)) {
    matter.operation
      .filter(
        (op) =>
          op.operationStatus === 'ERRORED' && relevantOperationTypes.includes(op.operationType),
      )
      .forEach((op) => {
        erroredOperations.push({
          id: op.id,
          operationType: op.operationType,
          operationStatus: op.operationStatus,
        });
      });
  }

  return erroredOperations;
};

// Get all errored operations for a specific tool (includes both file-level and client matter-level operations)
export const getErroredOperationsForTool = (
  toolIdentifier: string,
  matter: ClientMatterWithDetails,
) => {
  const erroredOperations: Array<{
    id: string;
    operationType: string;
    operationStatus: string;
  }> = [];

  // Get client matter level errors first
  erroredOperations.push(...getErroredOperationsForClientMatter(toolIdentifier, matter));

  const relevantOperationTypes = getOperationTypesForTool(toolIdentifier);

  // Safety checks for data structure
  if (!matter?.dataRoomFiles || !Array.isArray(matter.dataRoomFiles)) {
    return erroredOperations;
  }

  // Check operations on individual files
  matter.dataRoomFiles.forEach((file) => {
    // Safety check for file operations
    if (!file?.operation || !Array.isArray(file.operation)) {
      return;
    }

    file.operation
      .filter(
        (op) =>
          op.operationStatus === 'ERRORED' && relevantOperationTypes.includes(op.operationType),
      )
      .forEach((op) => {
        erroredOperations.push({
          id: op.id,
          operationType: op.operationType,
          operationStatus: op.operationStatus,
        });
      });
  });

  return erroredOperations;
};
